import { Injectable, computed, inject, signal } from '@angular/core';
import { Feature } from './feature';
import { Api, isOKResponse, Response } from '../api';

const emptyResponse = { state: 'empty', data: [] } as Response<Feature[]>;
const loadingResponse = { state: 'loading', data: [] } as Response<Feature[]>;


@Injectable({
    providedIn: 'root'
})
export class FeaturesService {

    private api = inject(Api);

    public featureResponse = signal<Response<Feature[]>>(emptyResponse);
    public features = computed(() => {
        const response = this.featureResponse();
        if (isOKResponse(response)) {
            return response.data;
        }
        return [];
    });
    public enabledFeatures = computed(() => this.features().filter(f => f.status.enabled));

    public labsFeatures = computed(() => this.features().filter(f => f.availableIn === 'labs'));
    public freeFeatures = computed(() => this.features().filter(f => f.availableIn === 'free'));
    public protectedFeatures = computed(() => this.features().filter(f => f.availableIn === 'protected'));

    public groupedFeatures = {
        labs: this.labsFeatures,
        free: this.freeFeatures,
        protected: this.protectedFeatures
    };

    private mapping = {
        'labs': this.labsFeatures,
        'free': this.freeFeatures,
        'protected': this.protectedFeatures
    }

    getByAvailableIn(availableIn: 'labs' | 'free' | 'protected') {
        return this.mapping[availableIn];
    }

    private fetchPromise: Promise<void> | null = null;

    async fetchFeatures() {
        if (this.fetchPromise) {
            return this.fetchPromise;
        }

        if (this.featureResponse().state === 'empty') {
            this.featureResponse.set(loadingResponse);
        }

        this.fetchPromise = this.doFetchFeatures();
        try {
            await this.fetchPromise;
        } finally {
            this.fetchPromise = null;
        }
    }

    private async doFetchFeatures() {
        const features = await this.api.get<Feature[]>('/features/')
        if (!isOKResponse(features)) {
            this.featureResponse.set({ state: 'error', error: features.error, data: [] });
            return;
        }
        features.data = features.data.map(f => new Feature(f));
        this.featureResponse.update((_currFeatures) => features);
    }

    async enableFeature(feature: Feature) {
        await this.api.post(`/features/${feature.id}/enable`);
        await this.fetchFeatures();
    }

    async disableFeature(feature: Feature) {
        await this.api.post(`/features/${feature.id}/disable`);
        await this.fetchFeatures();
    }

    async isEnabled(featureId: string) {
        if (this.featureResponse().state !== 'ok') {
            await this.fetchFeatures();
        }
        if (this.featureResponse().state !== 'ok') {
            return false;
        }

        const feature = this.features().find(f => f.id === featureId);
        return feature !== undefined ? feature.status.enabled : false;
    }

    async ensureFeatures() {
        if (this.featureResponse().state !== 'ok') {
            await this.fetchFeatures();
        }
    }

    invalidateFeatures() {
        this.featureResponse.set(emptyResponse);
    }
}
